import { clsx } from 'clsx';
import { useCallback, useState } from 'react';
import type { CSSProperties, ComponentPropsWithoutRef } from 'react';
import type { ImageDto } from '@sbg/shared/schemas';
import { fallbackSource, srcSetFor } from './sources';
import type { ImageFormat } from './sources';

const MIME: Readonly<Record<ImageFormat, string>> = {
  avif: 'image/avif',
  webp: 'image/webp',
  jpeg: 'image/jpeg',
};

/** Only ever paint a data URI we recognise as an image into CSS. */
const SAFE_LQIP = /^data:image\/(?:webp|jpeg|png|avif);base64,[A-Za-z0-9+/=]+$/;

export interface PictureProps extends Omit<
  ComponentPropsWithoutRef<'img'>,
  'src' | 'srcSet' | 'sizes' | 'alt' | 'width' | 'height' | 'children'
> {
  image: ImageDto;
  /**
   * How wide the image renders at each breakpoint (`(min-width: 1024px) 33vw, 100vw`). Required: the
   * browser picks a file from it before layout, and a wrong guess downloads the wrong one.
   */
  sizes: string;
  /** The LCP image: eager, high fetch priority, no lazy loading. */
  priority?: boolean;
  /**
   * Draw only the placeholder (the sized wrapper with its blurred preview and dominant colour) and leave the
   * `<picture>` out, so nothing is fetched yet. For an image nobody can use before the page is alive — a gallery
   * thumbnail is a button, and buttons do nothing before hydration. Same box, so nothing moves when it is turned off.
   */
  deferred?: boolean;
  /** Override the stored alt text. Pass `''` for decoration (textures, repeated imagery). */
  alt?: string;
  /** Class for the wrapping `<span>` (sizing, aspect ratio, radius). */
  className?: string;
  /** Class for the `<img>` itself. */
  imgClassName?: string;
}

/**
 * The one way content images appear (DESIGN_SYSTEM §2.1): AVIF → WebP → JPEG `srcset`, explicit
 * dimensions (no layout shift), and a blurred ~16 px placeholder that the sharp image fades over.
 *
 * The wrapper is what you size — the image fills it (`object-fit: cover`). Until the page has booted
 * (server HTML, no JavaScript) the image is simply visible; the fade only exists once scripting runs.
 */
export function Picture({
  image,
  sizes,
  priority = false,
  deferred = false,
  alt,
  className,
  imgClassName,
  style,
  onLoad,
  onError,
  ...rest
}: PictureProps) {
  const [settledId, setSettledId] = useState<string | null>(null);
  const settled = settledId === image.id;

  // An image that finished before React attached `onLoad` (cached, or from prerendered HTML) never
  // fires the event again: check `complete` when the element mounts.
  const imgRef = useCallback(
    (element: HTMLImageElement | null) => {
      if (element?.complete && element.naturalWidth > 0) setSettledId(image.id);
    },
    [image.id],
  );

  const fallback = fallbackSource(image.sources);
  const lqip = image.blurDataUrl && SAFE_LQIP.test(image.blurDataUrl) ? image.blurDataUrl : null;
  const wrapperStyle = {
    aspectRatio: `${String(image.width)} / ${String(image.height)}`,
    ...(lqip ? { backgroundImage: `url("${lqip}")` } : {}),
    ...(image.dominantColor ? { backgroundColor: image.dominantColor } : {}),
    ...style,
  } as CSSProperties;

  return (
    <span className={clsx('picture', className)} data-loaded={settled} style={wrapperStyle}>
      {deferred ? null : (
        <picture>
          {(['avif', 'webp'] as const).map((format) => {
            const srcSet = srcSetFor(image.sources, format);
            return srcSet ? (
              <source key={format} type={MIME[format]} srcSet={srcSet} sizes={sizes} />
            ) : null;
          })}
          <img
            {...rest}
            ref={imgRef}
            className={imgClassName}
            src={fallback?.url}
            srcSet={srcSetFor(image.sources, 'jpeg') || undefined}
            sizes={sizes}
            width={image.width}
            height={image.height}
            alt={alt ?? image.alt}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            {...(priority ? { fetchPriority: 'high' as const } : {})}
            onLoad={(event) => {
              setSettledId(image.id);
              onLoad?.(event);
            }}
            onError={(event) => {
              setSettledId(image.id); // never leave a broken image invisible: show the alt text
              onError?.(event);
            }}
          />
        </picture>
      )}
    </span>
  );
}
